// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasParentProductId, withParentProductId } from "@insite/client-framework/Components/ParentProductContext";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    getProductState,
    getVariantChildrenDataView,
} from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import { ProductListViewType } from "@insite/client-framework/Store/Pages/ProductList/ProductListState";
import translate from "@insite/client-framework/Translate";
import ProductAddToCartButton from "@insite/content-library/Components/ProductAddToCartButton";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import ProductQuantityOrdered from "@insite/content-library/Components/ProductQuantityOrdered";
import ProductUnitOfMeasureSelect from "@insite/content-library/Components/ProductUnitOfMeasureSelect";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import { SelectPresentationProps } from "@insite/mobius/Select";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ProductListGridContainer, {
    ProductListGridContainerStyle,
} from "@west/App/Components/Product/ProductListGridContainer";
import ProductNonReturnable, { ProductNonReturnableStyles } from "@west/App/Components/Product/ProductNonReturnable";
import UomMessage, { UomMessageStyles } from "@west/App/Components/Product/UomMessage";
import ProductListVariantOptions, {
    ProductListVariantOptionsStyles,
} from "@west/App/Components/ProductList/ProductListVariantOptions";
import { getSettingsCollection } from "@west/App/Store/Selectors/SettingsSelectors";
import {
    AuthenticationPrompt,
    AuthenticationPromptStyles,
} from "@west/App/Widgets/AuthenticationPrompt/AuthenticationPrompt";
import Tooltip, { TooltipPresentationProps } from "@west/MobiusOverrides/Tooltip";
import ProductAddToListLink, { ProductAddToListLinkStyles } from "@west/Overrides/Components/ProductAddToListLink";
import { ProductAvailabilityStyles } from "@west/Overrides/Components/ProductAvailability";
import ProductListProductImage, {
    ProductListProductImageStyles,
} from "@west/Overrides/Widgets/ProductList/ProductListProductImage";
import NotificationTooltip from "@west/XDL/Icons/NotificationTooltip";
import { mergeWithCss } from "@west/XDL/Utilities";
import React, { FC, useState } from "react";
import { connect } from "react-redux";

export interface ProductPriceAndActionsProps {
    showAddToList?: boolean;
    showAvailability?: boolean;
    showVariants?: boolean;
    showPrice?: boolean;
    showAddToCartButton?: boolean;
    showCompare?: boolean;
    showAddToCartActions?: boolean;
    extendedStyles?: ProductPriceAndActionsStyle;
    view: ProductListViewType;
    onAddToCartClick?: () => void;
    containerClassName?: string;
}

const mapStateToProps = (state: ApplicationState) => ({
    canSeePrices: getSettingsCollection(state).productSettings.canSeePrices && state.context.showPricing,
});

type Props = ProductPriceAndActionsProps & HasProductContext & ReturnType<typeof mapStateToProps>;

export interface ProductPriceAndActionsStyle {
    productListGridContainer?: ProductListGridContainerStyle;
    priceAndQtyContainer?: InjectableCss;
    productPriceStyles?: ProductPriceStyles;
    priceContainer?: InjectableCss;
    productFields?: InjectableCss;
    productUnitOfMeasureSelectStyles?: SelectPresentationProps;
    addToListLink?: ProductAddToListLinkStyles;
    productImageStyles?: ProductListProductImageStyles;
    productAvailableStyles?: ProductAvailabilityStyles;
    variantOptionsStyles?: ProductListVariantOptionsStyles;
    messagesContainer?: InjectableCss;
    uomMessageStyles?: UomMessageStyles;
    productNonReturnable?: ProductNonReturnableStyles;
    addToCartActions?: InjectableCss;
    infoAvailabilityWrapper?: InjectableCss;
    addToCartButton?: ButtonPresentationProps;
    authenticationPrompt?: AuthenticationPromptStyles;
    qtyDetailsTooltip?: TooltipPresentationProps;
    qtyDetailsTooltipList?: TooltipPresentationProps;
    qtyDetailsTooltipIcon?: IconPresentationProps;
}

export const productPriceAndActionsStyle: ProductPriceAndActionsStyle = {};

const ProductPriceAndActions: FC<Props> = ({
    showAddToList = true,
    showAvailability = true,
    showPrice = true,
    showVariants = true,
    extendedStyles,
    showAddToCartButton,
    showAddToCartActions, // Show Add to cart + uom + qty
    showCompare = false,
    productContext,
    onAddToCartClick,
    containerClassName,
    canSeePrices,
    view,
}) => {
    const [styles] = useState(() => mergeWithCss(productPriceAndActionsStyle, extendedStyles));
    const { product } = productContext;

    const QtyTooltip = ({ tooltipStyle }: { tooltipStyle?: TooltipPresentationProps }) => {
        const currentUnitOfMeasures = product.unitOfMeasures?.find(
            uom => productContext.productInfo.unitOfMeasure === uom.unitOfMeasure,
        );

        if (!currentUnitOfMeasures || product?.minimumOrderQty / currentUnitOfMeasures.qtyPerBaseUnitOfMeasure <= 1) {
            return null;
        }

        const message = [
            product.detail?.multipleSaleQty && `${translate("Qty Interval")}: ${product.detail.multipleSaleQty}`,
            product.minimumOrderQty && `${translate("Min Qty")}: ${product.minimumOrderQty}`,
        ]
            .filter(Boolean)
            .join(" | ");

        return (
            <Tooltip
                text={message}
                triggerComponent={<Icon {...styles.qtyDetailsTooltipIcon} src={NotificationTooltip} />}
                {...tooltipStyle}
            />
        );
    };

    return (
        <ProductListGridContainer
            extendedStyles={styles.productListGridContainer}
            className={containerClassName}
            data-test-selector="product-price-selector"
        >
            <ProductListGridContainer.Variant>
                {showVariants && view === "List" && <ProductListVariantOptions />}
            </ProductListGridContainer.Variant>
            <ProductListGridContainer.Info>
                <StyledWrapper {...styles.priceAndQtyContainer}>
                    {showPrice && canSeePrices && (
                        <StyledWrapper {...styles.priceContainer}>
                            <ProductPrice
                                product={productContext}
                                showLabel={false}
                                showSavings={true}
                                showPack={false}
                                {...styles.productPriceStyles}
                            />
                        </StyledWrapper>
                    )}
                    {showAddToCartButton && (
                        <>
                            <StyledWrapper {...styles.productFields}>
                                {view === "Grid" && <QtyTooltip tooltipStyle={styles.qtyDetailsTooltip} />}
                                {product.unitOfMeasures && (
                                    <ProductUnitOfMeasureSelect
                                        labelOverride=""
                                        extendedStyles={styles.productUnitOfMeasureSelectStyles}
                                        sizeVariant="small"
                                    />
                                )}
                                <ProductQuantityOrdered />
                            </StyledWrapper>
                            <StyledWrapper {...styles.messagesContainer} className="grid-view">
                                <UomMessage extendedStyled={styles.uomMessageStyles} />
                                <ProductNonReturnable extendedStyled={styles.productNonReturnable} />
                            </StyledWrapper>
                        </>
                    )}
                </StyledWrapper>
                {showAvailability && view === "List" && (
                    <ProductContextAvailability extendedStyles={styles.productAvailableStyles} />
                )}
            </ProductListGridContainer.Info>
            <ProductListGridContainer.AddToCartActions>
                {showAvailability && view === "Grid" && (
                    <ProductContextAvailability extendedStyles={styles.productAvailableStyles} />
                )}
                {showAddToCartButton && (
                    <ProductAddToCartButton
                        extendedStyles={styles.addToCartButton}
                        onAddToCartClick={onAddToCartClick}
                    />
                )}
                {showAddToCartActions && (
                    <>
                        <StyledWrapper {...styles.addToCartActions}>
                            <StyledWrapper {...styles.productFields}>
                                {view === "List" && <QtyTooltip tooltipStyle={styles.qtyDetailsTooltipList} />}
                                {product.unitOfMeasures && (
                                    <ProductUnitOfMeasureSelect
                                        labelOverride=""
                                        extendedStyles={styles.productUnitOfMeasureSelectStyles}
                                        sizeVariant="small"
                                    />
                                )}
                                <ProductQuantityOrdered />
                            </StyledWrapper>
                            <ProductAddToCartButton
                                extendedStyles={styles.addToCartButton}
                                onAddToCartClick={onAddToCartClick}
                            />
                        </StyledWrapper>
                        <StyledWrapper {...styles.messagesContainer} className="row-view">
                            <UomMessage extendedStyled={styles.uomMessageStyles} />
                            <ProductNonReturnable extendedStyled={styles.productNonReturnable} />
                        </StyledWrapper>
                    </>
                )}
            </ProductListGridContainer.AddToCartActions>
            <ProductListGridContainer.AddToList>
                <AuthenticationPrompt extendedStyles={styles.authenticationPrompt} />
                <ProductListProductImage
                    extendedStyles={styles.productImageStyles}
                    showImage={false}
                    showCompare={showCompare}
                />
                {showAddToList && <ProductAddToListLink extendedStyles={styles.addToListLink} />}
            </ProductListGridContainer.AddToList>
        </ProductListGridContainer>
    );
};

export default withProductContext(connect(mapStateToProps)(ProductPriceAndActions));
